import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getMenuConfig, joinUrl } from './MenuConfig';

const ChevronDownIcon = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ChevronUpIcon = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

const CloseIcon = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

ChevronDownIcon.propTypes = {
  className: PropTypes.string,
};

ChevronUpIcon.propTypes = {
  className: PropTypes.string,
};

CloseIcon.propTypes = {
  className: PropTypes.string,
};

const UnifiedHeader = ({ dashUrl, mainUrl, docsUrl, navbarLogo, rightSlot, mobileTopSlot, showLogin = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileOpenDropdowns, setMobileOpenDropdowns] = useState({});
  const [activeDesktopDropdown, setActiveDesktopDropdown] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const menuLinkBaseClassName = 'text-gray-700 hover:bg-gray-50 whitespace-nowrap';
  const menuListLinkClassName = `${menuLinkBaseClassName} px-4 py-1.5 rounded-md`;
  const menuInlineLinkClassName = `${menuLinkBaseClassName}`;
  const menuItemPropType = PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    flag: PropTypes.string,
  });

  const MENU_CONFIG = getMenuConfig({ mainUrl, docsUrl });

  const primaryNavItems = [
    {
      type: 'dropdown',
      id: 'solutions',
      label: MENU_CONFIG.solutions.label,
      minWidth: 240,
      sections: MENU_CONFIG.solutions.sections,
      defaultVariant: 'default',
    },
    ...MENU_CONFIG.navigation.map((item) => ({ type: 'link', item })),
    {
      type: 'dropdown',
      id: 'resources',
      label: MENU_CONFIG.resources.label,
      minWidth: 360,
      sections: MENU_CONFIG.resources.sections,
      defaultVariant: MENU_CONFIG.resources.variant,
    },
    {
      type: 'dropdown',
      id: 'about',
      label: MENU_CONFIG.company.label,
      minWidth: 180,
      items: MENU_CONFIG.company.items,
      defaultVariant: 'default',
    },
  ];

  const renderPrimaryNavItem = (entry) => {
    if (entry.type === 'link') {
      return <NavLink key={entry.item.label} item={entry.item} />;
    }

    return (
      <DesktopDropdown
        key={entry.id}
        id={entry.id}
        label={entry.label}
        minWidth={entry.minWidth}
        sections={entry.sections}
        items={entry.items}
        defaultVariant={entry.defaultVariant}
      />
    );
  };

  const loginButtonProps = {
    href: dashUrl,
    target: '_blank',
    rel: 'noopener noreferrer',
    className:
      'whitespace-nowrap rounded-full text-left outline-none ring-0 transition-all ' +
      'focus:border-none focus:outline-none font-medium flex items-center gap-3 px-5 py-2 ' +
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-95 ' +
      'duration-200 text-indigo-600 mr-2 border border-indigo-600 active:bg-indigo-100 active:border-indigo-600',
  };

  const demoButtonProps = {
    href: joinUrl(mainUrl, '/contact'),
    rel: 'noopener noreferrer',
    className:
      'whitespace-nowrap rounded-full text-left outline-none ring-0 transition-all ' +
      'focus:border-none focus:outline-none font-medium flex items-center gap-3 px-5 py-2 ' +
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-95 ' +
      'duration-200 bg-indigo-600 text-white hover:text-white',
  };

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setMobileOpenDropdowns({});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const topNav = document.getElementById('TopNav');
      if (!topNav) {
        return;
      }
      if (window.scrollY > 70) {
        topNav.style.top = '-100px';
      } else {
        topNav.style.top = '0';
      }
    };

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeMenu]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, isMenuOpen]);

  const toggleMenu = () => {
    if (isMenuOpen) {
      closeMenu();
      return;
    }

    setIsMenuOpen(true);
  };

  const toggleMobileDropdown = (id) => {
    setMobileOpenDropdowns((prev) => ({
      [id]: !prev[id],
    }));
  };

  const isMobileDropdownOpen = (id) => Boolean(mobileOpenDropdowns[id]);

  const NavLink = ({ item, isMobile = false, isSubItem = false, variant = 'default', className = '', onClick }) => {
    const sizeClass =
      variant === 'docs'
        ? isMobile
          ? 'text-[15px]'
          : 'text-[14px]'
        : isMobile
          ? isSubItem
            ? 'text-[15px]'
            : 'text-[17px]'
          : 'text-[15px]';
    return (
      <a
        className={`font-medium hoverable-black ${sizeClass} ${
          item.flag ? 'inline-flex items-center gap-1' : ''
        } ${className}`}
        href={item.href}
        onClick={onClick}
      >
        {item.flag ? (
          <img src={joinUrl(mainUrl, `/flags/${item.flag}.png`)} alt={`${item.label} flag`} className="w-4 h-4" />
        ) : null}
        {item.label}
      </a>
    );
  };

  NavLink.propTypes = {
    item: menuItemPropType.isRequired,
    isMobile: PropTypes.bool,
    isSubItem: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'docs']),
    className: PropTypes.string,
    onClick: PropTypes.func,
  };

  const InlineItem = ({ item, isMobile, sectionVariant, isLast }) => (
    <span className="flex items-center gap-2 whitespace-nowrap">
      <NavLink
        item={item}
        isMobile={isMobile}
        isSubItem={true}
        variant={sectionVariant}
        className={menuInlineLinkClassName}
      />
      {isLast ? null : <span className="text-gray-300">|</span>}
    </span>
  );

  InlineItem.propTypes = {
    item: menuItemPropType.isRequired,
    isMobile: PropTypes.bool.isRequired,
    sectionVariant: PropTypes.oneOf(['default', 'docs']).isRequired,
    isLast: PropTypes.bool.isRequired,
  };

  const ListItem = ({ item, isMobile, sectionVariant }) => (
    <NavLink
      item={item}
      isMobile={isMobile}
      isSubItem={true}
      variant={sectionVariant}
      className={
        isMobile
          ? 'flex min-h-[40px] w-full items-center rounded-lg px-3 py-2 text-left leading-snug hover:bg-white whitespace-normal'
          : menuListLinkClassName
      }
      onClick={isMobile ? closeMenu : undefined}
    />
  );

  ListItem.propTypes = {
    item: menuItemPropType.isRequired,
    isMobile: PropTypes.bool.isRequired,
    sectionVariant: PropTypes.oneOf(['default', 'docs']).isRequired,
  };

  const SectionHeader = ({ children, href, className = '', onClick }) =>
    href ? (
      <a
        href={href}
        className={`text-indigo-600 text-[12px] font-semibold uppercase tracking-wide px-4 underline ${className}`}
        onClick={onClick}
      >
        {children}
      </a>
    ) : (
      <div className={`text-indigo-600 text-[12px] font-semibold uppercase tracking-wide px-4 ${className}`}>
        {children}
      </div>
    );

  SectionHeader.propTypes = {
    children: PropTypes.node.isRequired,
    href: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
  };

  const SectionItems = ({ items, renderItem, className = '' }) => (
    <div className={className}>{items.map(renderItem)}</div>
  );

  SectionItems.propTypes = {
    items: PropTypes.arrayOf(menuItemPropType).isRequired,
    renderItem: PropTypes.func.isRequired,
    className: PropTypes.string,
  };

  const SectionDivider = ({ className = '' }) => <div className={`h-px bg-gray-200 ${className}`} />;

  SectionDivider.propTypes = {
    className: PropTypes.string,
  };

  const DropdownButton = ({ label, isOpen, onClick, variant = 'desktop' }) => {
    const baseClassName =
      variant === 'desktop'
        ? 'font-medium hoverable-black text-[15px] leading-none p-0 flex items-center gap-1 bg-transparent hover:bg-transparent text-gray-800'
        : 'font-medium hoverable-black flex min-h-[48px] w-full items-center justify-between rounded-lg bg-transparent px-3 text-left text-[17px] text-gray-900 hover:bg-gray-50';
    const icon =
      variant === 'desktop' ? (
        <ChevronDownIcon className="text-base h-4 w-4" />
      ) : isOpen ? (
        <ChevronUpIcon className="text-base h-4 w-4" />
      ) : (
        <ChevronDownIcon className="text-base h-4 w-4" />
      );

    return (
      <button
        type="button"
        className={baseClassName}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onClick}
        style={variant === 'desktop' ? { height: '-webkit-fill-available' } : undefined}
      >
        {label}
        {icon}
      </button>
    );
  };

  DropdownButton.propTypes = {
    label: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    onClick: PropTypes.func,
    variant: PropTypes.oneOf(['desktop', 'mobile']),
  };

  const resolveSections = ({ sections, items }) => sections || (items ? [{ items }] : []);

  const renderDropdownSections = ({ sections, isMobile, defaultVariant = 'default' }) => (
    <div className={isMobile ? 'mt-2 w-full space-y-4 rounded-xl bg-gray-50 px-3 py-4' : 'flex flex-col gap-1 pt-4'}>
      {sections.map((section, sectionIndex) => {
        const isInline = section.layout === 'inline' && !isMobile;
        const hasHeader = section.title && !section.titleHidden;
        const sectionVariant = section.variant || defaultVariant;
        const headerClassName = `${isMobile ? 'block w-full text-left' : ''} ${section.titleHref ? 'underline' : ''}${
          isMobile && section.mobileTitleClassName ? ` ${section.mobileTitleClassName}` : ''
        } ${isMobile ? '' : 'mt-0'}`.trim();
        const listClassName = isMobile ? 'flex w-full flex-col gap-1' : 'pt-2 pb-3 flex flex-col gap-1.5';
        const inlineClassName = isMobile
          ? 'px-4 flex flex-wrap items-center justify-center gap-2 max-w-[520px] pb-1'
          : 'px-4 pt-2 pb-4 flex flex-wrap items-center gap-2 max-w-[520px]';

        return (
          <div key={section.title || `section-${sectionIndex}`} className={isMobile ? 'w-full' : ''}>
            {hasHeader ? (
              <SectionHeader
                href={section.titleHref}
                className={headerClassName}
                onClick={isMobile ? closeMenu : undefined}
              >
                {section.title}
              </SectionHeader>
            ) : null}
            {isInline ? (
              <SectionItems
                className={inlineClassName}
                items={section.items}
                renderItem={(item, index) => (
                  <InlineItem
                    key={index}
                    item={item}
                    isMobile={isMobile}
                    sectionVariant={sectionVariant}
                    isLast={index === section.items.length - 1}
                  />
                )}
              />
            ) : (
              <SectionItems
                className={listClassName}
                items={section.items}
                renderItem={(item, index) => (
                  <ListItem key={index} item={item} isMobile={isMobile} sectionVariant={sectionVariant} />
                )}
              />
            )}
            {sectionIndex < sections.length - 1 ? (
              <SectionDivider className={isMobile ? 'my-3 w-full' : 'mx-4 my-2'} />
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const MobileDropdown = ({ id, label, items, sections, defaultVariant }) => {
    const isOpen = isMobileDropdownOpen(id);
    const resolvedSections = resolveSections({ sections, items });

    return (
      <div className="w-full">
        <DropdownButton label={label} isOpen={isOpen} onClick={() => toggleMobileDropdown(id)} variant="mobile" />
        {isOpen ? renderDropdownSections({ sections: resolvedSections, isMobile: true, defaultVariant }) : null}
      </div>
    );
  };

  MobileDropdown.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(menuItemPropType),
    sections: PropTypes.arrayOf(PropTypes.shape({})),
    defaultVariant: PropTypes.oneOf(['default', 'docs']),
  };

  const DesktopDropdown = ({ id, label, items, sections, minWidth, defaultVariant }) => {
    const isOpen = activeDesktopDropdown === id;
    const resolvedSections = resolveSections({ sections, items });

    return (
      <div
        className="relative"
        onMouseEnter={() => setActiveDesktopDropdown(id)}
        onMouseLeave={() => setActiveDesktopDropdown(null)}
      >
        <DropdownButton label={label} isOpen={isOpen} />
        <div
          className={`absolute left-0 top-full -mt-1 ${isOpen ? 'flex' : 'hidden'} z-50 flex-col bg-white border border-gray-200 rounded-xl shadow-lg py-3`}
          style={{ minWidth }}
        >
          {renderDropdownSections({ sections: resolvedSections, isMobile: false, defaultVariant })}
        </div>
      </div>
    );
  };

  DesktopDropdown.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(menuItemPropType),
    sections: PropTypes.arrayOf(PropTypes.shape({})),
    minWidth: PropTypes.number,
    containerClassName: PropTypes.string,
    defaultVariant: PropTypes.oneOf(['default', 'docs']),
  };

  const LoginButton = ({ isMobile = false }) => (
    <a
      {...loginButtonProps}
      className={`${loginButtonProps.className} ${
        isMobile ? 'mt-0 w-full justify-center px-5 py-3 text-[16px]' : ''
      }`.trim()}
      onClick={isMobile ? closeMenu : undefined}
    >
      Login
    </a>
  );

  LoginButton.propTypes = {
    isMobile: PropTypes.bool,
  };

  const DemoButton = ({ isMobile = false }) => (
    <a
      {...demoButtonProps}
      className={`${demoButtonProps.className} ${isMobile ? 'mt-0 w-full justify-center px-5 py-3 text-[16px]' : ''}`}
      onClick={isMobile ? closeMenu : undefined}
    >
      Book a demo
    </a>
  );

  DemoButton.propTypes = {
    isMobile: PropTypes.bool,
  };

  const MobileNavLink = ({ item }) => (
    <NavLink
      item={item}
      isMobile={true}
      className="flex min-h-[48px] w-full items-center rounded-lg px-3 text-left text-gray-900 hover:bg-gray-50"
      onClick={closeMenu}
    />
  );

  MobileNavLink.propTypes = {
    item: menuItemPropType.isRequired,
  };

  return (
    <nav className="navbar navbar--fixed-top fixed top-0 w-screen z-50 duration-300 p-0 h-[80px] bg-white" id="TopNav">
      <div id="TopNavContent" className="content w-full p-0 pt-5 pb-5 flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-10">
          {navbarLogo}
          <div className="hidden lg:flex flex-row justify-center gap-8">
            {primaryNavItems.map(renderPrimaryNavItem)}
          </div>
        </div>
        <div className="hidden lg:flex items-center gap-4">
          {rightSlot ? <div className="flex items-center gap-2">{rightSlot}</div> : null}
          {showLogin ? <LoginButton /> : null}
          <DemoButton />
        </div>
        <div className="lg:hidden flex items-center">
          <button onClick={toggleMenu} className="p-2" ref={buttonRef} type="button" aria-label="Open navigation menu">
            <div className="grid grid-cols-3 gap-1">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="w-1.5 h-1.5 bg-gray-400"></div>
              ))}
            </div>
          </button>
        </div>
      </div>
      {isMenuOpen && (
        <div
          ref={menuRef}
          className="lg:hidden fixed inset-0 z-50 w-screen h-[100dvh] overflow-y-auto bg-white text-gray-900"
        >
          <div className="mx-auto flex min-h-full w-full max-w-[480px] flex-col px-5 pb-6 pt-4">
            <div className="flex min-h-[56px] items-center justify-between">
              {navbarLogo}
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                aria-label="Close navigation menu"
                onClick={closeMenu}
              >
                <CloseIcon className="h-6 w-6" />
              </button>
            </div>
            {mobileTopSlot ? <div className="mt-3 w-full">{mobileTopSlot}</div> : null}
            <div className="mt-4 flex flex-col gap-1 border-y border-gray-200 py-3">
              <MobileNavLink item={{ label: 'Home', href: joinUrl(mainUrl, '/') }} />
              {primaryNavItems.map((entry) =>
                entry.type === 'link' ? (
                  <MobileNavLink key={entry.item.label} item={entry.item} />
                ) : (
                  <MobileDropdown
                    key={entry.id}
                    id={entry.id}
                    label={entry.label}
                    items={entry.items}
                    sections={entry.sections}
                    defaultVariant={entry.defaultVariant}
                  />
                ),
              )}
            </div>
            <div className="mt-auto flex flex-col gap-3 pt-5">
              <DemoButton isMobile={true} />
              {showLogin ? <LoginButton isMobile={true} /> : null}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

UnifiedHeader.propTypes = {
  dashUrl: PropTypes.string.isRequired,
  mainUrl: PropTypes.string.isRequired,
  docsUrl: PropTypes.string.isRequired,
  navbarLogo: PropTypes.node.isRequired,
  rightSlot: PropTypes.node,
  mobileTopSlot: PropTypes.node,
  showLogin: PropTypes.bool,
};

export default UnifiedHeader;
